import React, { useState } from 'react';
import ErrorBoundary from '../components/ErrorBoundary';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, List, Typography, Divider,
  IconButton, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Menu, MenuItem, Tooltip, Collapse, useTheme, useMediaQuery,
  Select, FormControl, Chip, Stack,
} from '@mui/material';
import {
  LayoutDashboard, User, UserPlus, Settings,
  Menu as MenuIcon, LogOut, ShieldCheck,
  Sun, Moon, ShoppingCart, BarChart3,
  ChevronLeft, ChevronRight,
  ChevronDown, ChevronUp,
  Store, Layers, TrendingUp,
} from 'lucide-react';
import { useThemeMode } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useBranch } from '../contexts/BranchContext';
import logo from '../assets/roshan-grams-white-logo.png';
import logoDark from '../assets/roshan-grams-black-logo.png';

// Components
import Home from '../components/Home';
import Staff from '../components/Staff';
import BillingSettings from '../components/BillingSettings';
import ModuleManagement from '../components/ModuleManagement';
import Expenses from '../components/Expenses';
import Reports from '../components/Reports';
import Branches from '../components/Branches';
import Products from '../components/Products';
import MonthlyPnL from '../components/MonthlyPnL';

const DRAWER_WIDTH = 256;
const COLLAPSED_WIDTH = 68;

// ── Navigation structure ──────────────────────────────────────────────────────
const NAV_SECTIONS = [
  {
    id: 'main',
    alwaysOpen: true,
    items: [
      { path: '/dashboard', icon: <LayoutDashboard size={18} />, label: 'Dashboard', module: 'dashboard' },
      { path: '/products', icon: <Layers size={18} />, label: 'Products', module: 'inventory' },
      { path: '/expenses', icon: <ShoppingCart size={18} />, label: 'Expenses', module: 'expenses' },
      { path: '/monthly-pnl', icon: <TrendingUp size={18} />, label: 'Monthly P&L', module: 'monthlyPnL' },
      { path: '/reports', icon: <BarChart3 size={18} />, label: 'Reports', module: 'reports' },
    ],
  },
  {
    id: 'system',
    label: 'System',
    defaultOpen: false,
    items: [
      { path: '/branches', icon: <Store size={18} />, label: 'Branches', module: 'branches' },
      { path: '/staff', icon: <UserPlus size={18} />, label: 'Staff', module: 'staff' },
      { path: '/settings', icon: <Settings size={18} />, label: 'Settings', module: 'settings' },
      { path: '/modules', icon: <ShieldCheck size={18} />, label: 'Module Access', module: 'modules' },
    ],
  },
];

const Dashboard = ({ user, permissions, onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const s = localStorage.getItem('sidebarOpen');
    return s !== null ? JSON.parse(s) : true;
  });
  const [sectionOpen, setSectionOpen] = useState(() => {
    try {
      const s = localStorage.getItem('sidebarSections');
      if (s) return JSON.parse(s);
    } catch {}
    const d = {};
    NAV_SECTIONS.forEach((sec) => { d[sec.id] = sec.defaultOpen ?? true; });
    return d;
  });
  const [anchorEl, setAnchorEl] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { mode, toggleTheme } = useThemeMode();
  const { language, toggleLanguage } = useLanguage();
  const { branches, activeBranch, switchBranch } = useBranch();

  const isAdmin = user?.role === 'admin' || permissions?.userType === 'admin';

  const hasPermission = (item) => {
    if (isAdmin) return true;
    return permissions?.[item.module] === true;
  };

  const toggleSection = (id) => {
    setSectionOpen((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem('sidebarSections', JSON.stringify(next));
      return next;
    });
  };

  const handleSidebarToggle = () => {
    const next = !sidebarOpen;
    setSidebarOpen(next);
    localStorage.setItem('sidebarOpen', JSON.stringify(next));
  };

  const currentLogo = theme.palette.mode === 'dark' ? logoDark : logo;

  const itemSx = (isActive) => ({
    borderRadius: 1.5,
    color: isActive ? theme.palette.primary.main : 'inherit',
    justifyContent: sidebarOpen ? 'flex-start' : 'center',
    px: sidebarOpen ? 1.5 : 1,
    py: 0.75,
    borderLeft: '3px solid',
    borderLeftColor: isActive ? theme.palette.primary.main : 'transparent',
    backgroundColor: isActive ? `${theme.palette.primary.main}14` : 'transparent',
    '&:hover': {
      backgroundColor: isActive
        ? `${theme.palette.primary.main}1e`
        : theme.palette.action.hover,
    },
    transition: 'all 0.15s',
  });

  // ── Sidebar content ──────────────────────────────────────────────────────────
  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <Box
        onClick={() => navigate('/dashboard')}
        sx={{
          p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', borderBottom: `1px solid ${theme.palette.divider}`, minHeight: 64,
        }}
      >
        {sidebarOpen ? (
          <Box
            component="img" src={currentLogo} alt="Roshan Murukku"
            sx={{ width: '100%', maxWidth: 140, height: 'auto', objectFit: 'contain' }}
          />
        ) : (
          <Box sx={{
            width: 36, height: 36, borderRadius: '50%',
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: theme.palette.primary.contrastText, fontWeight: 800, fontSize: '1rem',
          }}>
            R
          </Box>
        )}
      </Box>

      {/* Branch selector — admin only */}
      {isAdmin && sidebarOpen && branches.length > 0 && (
        <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5, display: 'block' }}>
            Viewing Branch
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={activeBranch?.id || ''}
              onChange={(e) => switchBranch(e.target.value)}
              sx={{ fontSize: '0.8rem', fontWeight: 600 }}
            >
              {branches.map((b) => (
                <MenuItem key={b.id} value={b.id} sx={{ fontSize: '0.85rem' }}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Staff branch indicator */}
      {!isAdmin && activeBranch && sidebarOpen && (
        <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.25 }}>
            Your Branch
          </Typography>
          <Chip
            icon={<Store size={12} />}
            label={activeBranch.name}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.75rem', fontWeight: 600 }}
          />
        </Box>
      )}

      {/* Nav items */}
      <List sx={{ px: sidebarOpen ? 1 : 0.5, py: 1.5, flex: 1, overflowY: 'auto' }}>
        {NAV_SECTIONS.map((section, sIdx) => {
          const visibleItems = section.items.filter((item) => hasPermission(item));
          if (visibleItems.length === 0) return null;

          const isExpanded = section.alwaysOpen || sectionOpen[section.id] !== false;
          const sectionHasActive = visibleItems.some((item) => item.path === location.pathname);
          const effectivelyOpen = isExpanded || sectionHasActive;

          return (
            <Box key={section.id} sx={{ mb: 0.5 }}>
              {sidebarOpen && section.label && (
                <ListItemButton
                  onClick={() => toggleSection(section.id)}
                  sx={{ borderRadius: 1.5, px: 1.5, py: 0.5, mb: 0.25 }}
                >
                  <ListItemText
                    primary={section.label}
                    primaryTypographyProps={{
                      fontSize: '0.68rem', fontWeight: 700,
                      letterSpacing: '0.08em', textTransform: 'uppercase',
                      color: sectionHasActive ? theme.palette.primary.main : theme.palette.text.disabled,
                    }}
                  />
                  <Box sx={{ color: theme.palette.text.disabled, display: 'flex' }}>
                    {effectivelyOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  </Box>
                </ListItemButton>
              )}

              <Collapse in={effectivelyOpen || !sidebarOpen} timeout={200}>
                {visibleItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Tooltip key={item.path} title={!sidebarOpen ? item.label : ''} placement="right">
                      <ListItem disablePadding sx={{ mb: 0.25 }}>
                        <ListItemButton
                          onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false); }}
                          sx={itemSx(isActive)}
                        >
                          <ListItemIcon sx={{
                            color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                            minWidth: sidebarOpen ? 32 : 'auto',
                            justifyContent: 'center',
                          }}>
                            {item.icon}
                          </ListItemIcon>
                          {sidebarOpen && (
                            <ListItemText
                              primary={item.label}
                              primaryTypographyProps={{ fontWeight: isActive ? 600 : 400, fontSize: '0.875rem' }}
                            />
                          )}
                        </ListItemButton>
                      </ListItem>
                    </Tooltip>
                  );
                })}
              </Collapse>

              {sIdx < NAV_SECTIONS.length - 1 && sidebarOpen && (
                <Divider sx={{ mt: 0.75, mb: 0.25, opacity: 0.4 }} />
              )}
            </Box>
          );
        })}
      </List>

      {/* Footer */}
      <Box sx={{
        px: 2, py: 1.5, borderTop: `1px solid ${theme.palette.divider}`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
      }}>
        {sidebarOpen ? (
          <>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem', lineHeight: 1.4 }}>Developed by</Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.68rem', fontWeight: 700, lineHeight: 1.4 }}>IPYNB Solutions</Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem', lineHeight: 1.4 }}>+94 72 421 8840</Typography>
          </>
        ) : (
          <Tooltip title="IPYNB Solutions · +94 72 421 8840" placement="right">
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', fontWeight: 700 }}>IP</Typography>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Top AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${sidebarOpen ? DRAWER_WIDTH : COLLAPSED_WIDTH}px)` },
          ml: { md: `${sidebarOpen ? DRAWER_WIDTH : COLLAPSED_WIDTH}px` },
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: `0 1px 0 ${theme.palette.divider}`,
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar sx={{ minHeight: 56 }}>
          <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 2, display: { md: 'none' } }}>
            <MenuIcon />
          </IconButton>

          <Tooltip title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
            <IconButton color="inherit" onClick={handleSidebarToggle} sx={{ mr: 2, display: { xs: 'none', md: 'flex' } }}>
              {sidebarOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
            </IconButton>
          </Tooltip>

          {/* Active branch badge in topbar (mobile / collapsed) */}
          {activeBranch && (
            <Chip
              icon={<Store size={13} />}
              label={activeBranch.name}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ fontWeight: 600, fontSize: '0.75rem', display: { md: sidebarOpen ? 'none' : 'flex' } }}
            />
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title={mode === 'dark' ? 'Light mode' : 'Dark mode'}>
            <IconButton onClick={toggleTheme} color="inherit" sx={{ mr: 1 }}>
              {mode === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </IconButton>
          </Tooltip>

          <Tooltip title={language === 'en' ? 'Tamil' : 'English'}>
            <IconButton onClick={toggleLanguage} color="inherit" sx={{ mr: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                {language === 'en' ? 'TA' : 'EN'}
              </Typography>
            </IconButton>
          </Tooltip>

          <Tooltip title="Account">
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: '50%',
                border: `1.5px solid ${theme.palette.divider}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s',
                '&:hover': { borderColor: theme.palette.primary.main, backgroundColor: theme.palette.action.hover },
              }}>
                <User size={17} />
              </Box>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{ sx: { mt: 1.5, minWidth: 210, borderRadius: 2, boxShadow: theme.shadows[8] } }}
          >
            <MenuItem disabled sx={{ opacity: 1 }}>
              <Box sx={{ py: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>{user?.email}</Typography>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                  <Chip
                    label={isAdmin ? 'Admin' : 'Staff'}
                    size="small"
                    color={isAdmin ? 'primary' : 'default'}
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                  {activeBranch && (
                    <Chip
                      label={activeBranch.name}
                      size="small"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  )}
                </Stack>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => { setAnchorEl(null); onLogout(); navigate('/login'); }}
              sx={{ color: 'primary.main' }}
            >
              <ListItemIcon><LogOut size={17} color="currentColor" /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box component="nav" sx={{ width: { md: sidebarOpen ? DRAWER_WIDTH : COLLAPSED_WIDTH }, flexShrink: { md: 0 }, transition: 'all 0.3s ease' }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: sidebarOpen ? DRAWER_WIDTH : COLLAPSED_WIDTH,
              transition: 'width 0.3s ease',
              boxSizing: 'border-box',
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: { md: `calc(100% - ${sidebarOpen ? DRAWER_WIDTH : COLLAPSED_WIDTH}px)` },
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar sx={{ minHeight: '56px !important' }} />
        <Routes>
          <Route path="/dashboard" element={<ErrorBoundary><Home permissions={permissions} user={user} /></ErrorBoundary>} />
          <Route path="/products" element={<ErrorBoundary><Products currentUser={user} permissions={permissions} /></ErrorBoundary>} />
          <Route path="/expenses" element={<ErrorBoundary><Expenses currentUser={user} permissions={permissions} /></ErrorBoundary>} />
          <Route path="/reports" element={<ErrorBoundary><Reports permissions={permissions} /></ErrorBoundary>} />
          <Route path="/monthly-pnl" element={<ErrorBoundary><MonthlyPnL currentUser={user} permissions={permissions} /></ErrorBoundary>} />
          <Route path="/branches" element={<ErrorBoundary><Branches currentUser={user} /></ErrorBoundary>} />
          <Route path="/staff" element={<ErrorBoundary><Staff currentUser={user} /></ErrorBoundary>} />
          <Route path="/settings" element={<ErrorBoundary><BillingSettings /></ErrorBoundary>} />
          <Route path="/modules" element={<ErrorBoundary><ModuleManagement currentUser={user} /></ErrorBoundary>} />
        </Routes>
      </Box>
    </Box>
  );
};

export default Dashboard;
